import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';

import { SnTip } from '@lib/api/entities';
import { FsMapTipsServiceProvider, IFsMap } from '@lib/geo/core';
import { FsTipAdvancedMarker, TipsServiceInterface } from '@lib/tips';
import { SnMapClusterPoint } from 'libs/api/src/lib/interfaces/map-response.interface';

@Injectable()
export class MapTipsFacadeService<TMap extends IFsMap = IFsMap>
  extends FsMapTipsServiceProvider<TMap>
  implements OnDestroy
{
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly provider: FsMapTipsServiceProvider<TMap>,
    private readonly tipsService: TipsServiceInterface,
  ) {
    super();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public get viewTipDetails$(): Observable<SnTip> {
    return this.tipsService.viewTipDetails$;
  }

  public get isAvailable(): boolean {
    return this.tipsService.isAvailable();
  }

  public get selected(): SnTip {
    return this.tipsService.selectedTip;
  }

  public get isSlewing(): boolean {
    return this.tipsService.isSlewing;
  }

  public set isSlewing(value: boolean) {
    this.tipsService.setIsSlewing(value);
  }

  public get tipsClustering(): boolean {
    return this.tipsService.tipsClustering;
  }

  public set tipsClustering(value: boolean) {
    this.tipsService.setTipsClustering(value);
  }

  public get results(): SnTip[] {
    return this.tipsService.results;
  }

  public set results(value: SnTip[]) {
    this.tipsService.updateResults(value);
  }

  public get clusterResults(): SnMapClusterPoint[] {
    return this.tipsService.clusterResults;
  }

  public set clusterResults(value: SnMapClusterPoint[]) {
    this.tipsService.updateClusterResults(value);
  }

  public get tipAdvancedMarker(): FsTipAdvancedMarker[] {
    return this.provider.tipAdvancedMarker;
  }

  public set tipAdvancedMarker(value: FsTipAdvancedMarker[]) {
    this.provider.tipAdvancedMarker = value;
  }

  public get tipAdvancedClusterMarker(): FsTipAdvancedMarker[] {
    return this.provider.tipAdvancedClusterMarker;
  }

  public set tipAdvancedClusterMarker(value: FsTipAdvancedMarker[]) {
    this.provider.tipAdvancedClusterMarker = value;
  }

  public onMapLoaded(map: TMap): void {
    this.provider.onMapLoaded(map);
  }

  public isSelected(id: string): boolean {
    return this.tipsService.isSelected(id);
  }

  public select(id: string): void {
    this.tipsService
      .select(id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((tip) => {
        this.tipsService.setSelectedTip(tip);
      });
  }

  public deselect(): void {
    this.tipsService.deselect();
  }

  public addToMap(): void {
    this.provider.addToMap();
  }

  public removeFromMap(): void {
    this.provider.removeFromMap();
  }

  public viewTipDetails(id: string): void {
    this.tipsService.viewTipDetails(id);
  }

  public setTipsClustering(enabled: boolean): void {
    this.tipsService.setTipsClustering(enabled);
  }
}
